import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import {
  AutocompleteScrollListbox,
  autocompleteScrollPaperProps,
} from "components/common/AutocompleteScrollListbox";
import {
  ClientItem,
  useGetIntegrationClientsQuery,
} from "features/integrationsBeta/registration";
import { useEffect, useMemo, useState } from "react";

interface Props {
  value: ClientItem | null;
  onChange: (client: ClientItem | null) => void;
  disabled?: boolean;
}

/**
 * Server-searchable client picker over GET /integration-platform/clients/
 * (core Client rows). Typing filters by company name or code on the server;
 * clients without a backend user are flagged in the options.
 */
export default function IntegrationClientSelect({
  value,
  onChange,
  disabled = false,
}: Props) {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(handle);
  }, [input]);

  const { data: clients = [], isFetching } = useGetIntegrationClientsQuery({
    search: search || undefined,
    page_size: 50,
  });

  // Keep the selected value visible even when the search no longer matches it.
  const options = useMemo(() => {
    if (value && !clients.some((c) => c.id === value.id)) {
      return [value, ...clients];
    }
    return clients;
  }, [clients, value]);

  return (
    <Autocomplete<ClientItem>
      options={options}
      value={value}
      onChange={(_, picked) => onChange(picked)}
      inputValue={input}
      onInputChange={(_, text) => setInput(text)}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={isFetching}
      disabled={disabled}
      ListboxComponent={AutocompleteScrollListbox}
      componentsProps={autocompleteScrollPaperProps}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">{option.name}</Typography>
            {option.has_backend_user === false && (
              <Typography variant="caption" color="warning.main">
                No backend user
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} size="small" label="Client" required />
      )}
    />
  );
}
